import React, { useEffect } from 'react'

import { Image, FadeSwap, Loader, Modal, Carousel } from '@pb/components'
import { CarouselOverflow, PrimaryButton } from '@hm-group/fabric-components'
import { BarcodeIcon } from '@hm-group/fabric-icons'
import { getLabelFromStorage, useMediaQuery, Breakpoints } from '@pb/utils'
import { useDispatch, useSelector } from 'react-redux'

import appSelectors from 'App/duck/selectors'
import { operationFetchMembershipInfo } from 'features/account/new-member-card/duck/operations'
import memberSelectors from 'features/account/new-member-card/duck/selectors'
import OPTIMIZELY from 'constants/optimizely'
import useOptimizelyToggle from 'features/account/hooks/useOptimizelyToggle'
import cn from 'classnames'

import Cards from './components/Cards'
import MemberCard from './components/MemberCard'
import styles from './NewMemberCardNS.scss'
import MemberCards from './types'
import CardBack from '../club-card/components/CardBack'
import CardBackTitle from '../club-card/components/CardBackTitle'
import IconLinkList from '../club-card/components/IconLinkList/IconLinkList'
import { operationToggleClubCard } from '../club-card/duck/operations'
import cardSelectors from '../club-card/duck/selectors'

const renderCards = (cards: MemberCards[], isSmallDevice): React.ReactElement[] =>
  (isSmallDevice
    ? cards
    : cards?.filter(
        (cardDetails) =>
          cardDetails.subText2Cta !== 'viewAllRewards' &&
          cardDetails.subText3Cta !== 'viewAllRewards',
      )
  )?.map(
    ({
      headline,
      subText1,
      subText2,
      subText3,
      iconPath,
      subText1Cta,
      subText2Cta,
      subText3Cta,
    }) => {
      return (
        <Cards
          headline={headline}
          subText1={subText1}
          subText2={subText2}
          subText3={subText3}
          iconPath={iconPath}
          subText1Cta={subText1Cta}
          subText2Cta={subText2Cta}
          subText3Cta={subText3Cta}
        />
      )
    },
  )

const NewMemberCardNS: React.FC = () => {
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(operationFetchMembershipInfo())
  }, [])

  const actionToggleClubCard = (): void => {
    dispatch(operationToggleClubCard())
  }
  const newMemberData = useSelector(memberSelectors.getMembershipDetail)
  const isLoading = useSelector(memberSelectors.getIsMembershipDetailLoading)
  const shouldShowQuickAccessButtons = useMediaQuery(Breakpoints.LARGE)
  const shouldShowClubCard = useSelector(cardSelectors.getShouldShowClubCard)
  const isClubCardDrawerOpen = useSelector(cardSelectors.getIsClubCardDrawerOpen)
  const name = useSelector(appSelectors.getUserDisplayName)
  const email = useSelector(appSelectors.getUserEmail)
  const currentBalance = useSelector(cardSelectors.getCurrentBalance) || 0
  const isSmallDevice = useMediaQuery(Breakpoints.MEDIUM)
  const isXXLDevice = useMediaQuery(Breakpoints.XXL)

  const labels = { next: 'next', prev: 'previous' }
  type NumOfSlides = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12
  const defaultNumOfItemsPerSlide: {
    default: NumOfSlides
    xsmall: NumOfSlides
    small: NumOfSlides
    medium: NumOfSlides
    large: NumOfSlides
    xlarge: NumOfSlides
    xxl: NumOfSlides
  } = {
    default: 1,
    xsmall: 1,
    small: 1,
    medium: 2,
    large: 2,
    xlarge: 4,
    xxl: 5,
  }
  const numOfItemsPerSlide = {
    xsmall: 1.1,
    small: 1.1,
    medium: 2,
    large: 2.3,
    xlarge: 4,
    xxl: 5,
  }
  const newCarouselEnabled = useOptimizelyToggle({
    key: OPTIMIZELY.ACCOUNT_MEMBERSHIP_LINKS,
  })
  const separateImagesEnabled = useOptimizelyToggle({
    key: OPTIMIZELY.ACCOUNT_MEMBERSHIP_IMAGES,
  })
  if (shouldShowClubCard) {
    return (
      <FadeSwap flag={isLoading} placeholder={<Loader />}>
        <Modal
          closeButtonText={getLabelFromStorage('general.close')}
          isOpen={isClubCardDrawerOpen}
          toggleModal={actionToggleClubCard}
          className={styles.sideDrawer}
          modalType="stickyLeft"
          isWithoutBackgroundColor
          customTitle={<CardBackTitle name={name} email={email} />}
        >
          <CardBack currentBalance={currentBalance} />
        </Modal>
        <PrimaryButton
          isReverse
          hasFullWidth
          iconElement={<BarcodeIcon />}
          iconPosition="end"
          className={styles.viewMemberId}
          onClick={actionToggleClubCard}
        >
          {getLabelFromStorage('view.your.member.id')}
        </PrimaryButton>
        <div className={styles.cardContainer}>
          {separateImagesEnabled ? (
            <Image
              url={
                isSmallDevice
                  ? newMemberData?.mobileBackgroundImage
                  : newMemberData?.desktopBackgroundImage
              }
              altText={getLabelFromStorage(
                'myaccount.membership.card.backgroud.image.accessibility',
              )}
            />
          ) : (
            <Image
              url={newMemberData?.backgroundImagePath}
              altText={getLabelFromStorage(
                'myaccount.membership.card.backgroud.image.accessibility',
              )}
            />
          )}
          {newCarouselEnabled ? (
            <div className={cn(styles.carousel, isXXLDevice && styles.largeCarousel)}>
              <CarouselOverflow
                labels={labels}
                slidesPerBreakpoint={defaultNumOfItemsPerSlide}
                slideSpacing="xsmall"
              >
                <MemberCard />
                {renderCards(newMemberData?.cards, isSmallDevice)}
              </CarouselOverflow>
            </div>
          ) : (
            <div className={styles.oldCarousel}>
              <Carousel
                labels={labels}
                numOfItemsPerSlide={numOfItemsPerSlide}
                isInfinite={false}
                onlyDots
                shouldRenderEmptySlides={false}
                dotClassName={styles.dotsAdjustment}
                slickClassName={styles.carouselAdjustment}
              >
                <MemberCard />
                {renderCards(newMemberData?.cards, isSmallDevice)}
              </Carousel>
            </div>
          )}
        </div>
        {shouldShowQuickAccessButtons && <IconLinkList />}
      </FadeSwap>
    )
  }
  return null
}
export default NewMemberCardNS
